import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useApi, errMsg, fmtTime } from "../lib/api";
import { GROUP_TYPES } from "../lib/constants";
import { useMe } from "../components/AppShell";
import { PageHeader, Empty } from "../components/common";
import { Invoice } from "./group/Orders";
import SiteManager from "./SiteManager";
import AdminConsole from "./AdminConsole";

const PRICE_KEYS = { group_subscription: "Group subscription", solo_subscription: "Solo subscription", wallet_card: "Wallet card", snap_wristband: "Snap wristband", wide_band: "Wide band", thin_band: "Thin line band", pet_tag: "Pet collar tag", tee: "bsafe Tee", cap: "bsafe Cap", vest: "Hi-vis Rider Vest", hoodie: "bsafe Hoodie", polo: "bsafe Team Polo", partner_product: "Partner product", egift_card: "eGift card" };

function Prices() {
  const api = useApi();
  const [prices, setPrices] = useState(null);
  useEffect(() => { api.get("/prices").then((r) => setPrices(r.data.prices)); }, [api]);
  if (!prices) return null;
  const save = async (cur) => { try { await api.put(`/admin/prices/${cur}`, prices[cur]); toast.success(`${cur} prices saved`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid md:grid-cols-2 gap-4">{Object.keys(prices).map((cur) => (
      <div key={cur} className="panel p-5" data-testid={`prices-${cur}`}><div className="overline mb-3">{cur} · placeholder prices</div>
        {Object.entries(PRICE_KEYS).map(([k, l]) => <div key={k} className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-slate-300">{l}</span>
          <Input data-testid={`price-${cur}-${k}`} type="number" className="w-36 h-8" value={prices[cur][k]} onChange={(e) => setPrices({ ...prices, [cur]: { ...prices[cur], [k]: e.target.value } })} /></div>)}
        <Button size="sm" className="mt-3" data-testid={`save-prices-${cur}`} onClick={() => save(cur)}>Save {cur}</Button>
      </div>))}</div>
  );
}

export function Invoices({ onChange }) {
  const api = useApi();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("pending_payment");
  const load = useCallback(() => api.get("/admin/orders", { params: { status: status === "all" ? "" : status } }).then((r) => setOrders(r.data)), [api, status]);
  useEffect(() => { load(); }, [load]);
  const paid = async (o) => {
    const reference = window.prompt(`Payment reference for ${o.invoice_no} (${{ mobile_money: "mobile money transaction ID", bank_transfer: "bank reference", cash: "cash receipt number", paypal: "PayPal transaction ID – check it in your PayPal account first" }[o.payment_method] || "reference"})`, o.payer_reference || "");
    if (reference === null) return;
    try { await api.post(`/admin/orders/${o.id}/mark-paid`, { reference }); toast.success("Marked paid – tags generated and sent to print queue"); load(); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-56 mb-4" data-testid="admin-invoice-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="pending_payment">Awaiting payment</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="shipped">Shipped</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
      {orders.length === 0 && <Empty testid="admin-invoices-empty">No invoices.</Empty>}
      <div className="grid lg:grid-cols-2 gap-4">{orders.map((o) => (
        <div key={o.id}><Invoice o={o} />
          {o.status === "pending_payment" && <Button data-testid={`mark-paid-${o.invoice_no}`} className="mt-2 w-full bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => paid(o)}>Mark as paid</Button>}</div>))}</div>
    </div>
  );
}

function Users() {
  const api = useApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const setRole = async (uid, role) => { try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel p-5 overflow-x-auto"><table className="w-full text-sm" data-testid="admin-users-table">
      <thead><tr className="text-left overline border-b border-slate-700"><th className="pb-2">Email</th><th className="pb-2">Name</th><th className="pb-2">Phone</th><th className="pb-2">Role</th></tr></thead>
      <tbody>{data.users.map((u) => <tr key={u.user_id} className="border-b border-slate-800"><td className="py-2">{u.email}</td><td>{u.name}</td><td className="font-mono text-xs">{u.phone}</td>
        <td className="py-2"><Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}><SelectTrigger className="h-8 w-36" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
          <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{r === "admin" ? "super admin" : r.replace("_", " ")}</SelectItem>)}</SelectContent></Select></td></tr>)}</tbody></table></div>
  );
}

export default function SuperAdmin() {
  const api = useApi();
  const { me } = useMe();
  const [ov, setOv] = useState(null);
  const loadOv = useCallback(() => api.get("/admin/overview").then((r) => setOv(r.data)), [api]);
  useEffect(() => { if (me.role === "admin") loadOv(); }, [loadOv, me.role]);
  if (me.role !== "admin") return <Empty testid="admin-not-allowed">Super admin only.</Empty>;
  return (
    <div data-testid="super-admin-page">
      <PageHeader overline="All organisations and individuals on bsafe" title="Admin console" />
      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto"><TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger><TabsTrigger value="invoices" data-testid="admin-tab-invoices">Invoices</TabsTrigger><TabsTrigger value="prices" data-testid="admin-tab-prices">Prices</TabsTrigger>
          <TabsTrigger value="groups" data-testid="admin-tab-groups">All groups</TabsTrigger><TabsTrigger value="users" data-testid="admin-tab-users">Platform admins</TabsTrigger><TabsTrigger value="website" data-testid="admin-tab-website">Website</TabsTrigger></TabsList>
        <TabsContent value="overview"><AdminConsole /></TabsContent>
        <TabsContent value="invoices"><Invoices onChange={loadOv} /></TabsContent>
        <TabsContent value="prices"><Prices /></TabsContent>
        <TabsContent value="groups"><div className="panel p-5">{(ov?.groups || []).map((g) => (
          <div key={g.id} className="flex justify-between py-2 border-b border-slate-800 text-sm"><span><span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ background: GROUP_TYPES[g.type]?.color }} />{g.name}</span>
            <span className="text-slate-400">{g.type} · {g.country} · {g.members} members · {fmtTime(g.created_at)}</span></div>))}</div></TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="website"><SiteManager /></TabsContent>
      </Tabs>
    </div>
  );
}
